import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Wave 1B.2A — Capture `?rc=` (research-candidate token) into sessionStorage.
// Attribution-only — the token is NEVER an authorization credential and the
// server rejects any token not present in the `cohort_registry` allowlist.
// See PRE_COHORT_TRACEABILITY_READINESS_AUDIT.md §22.7.
const RC_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,39}$/;
function captureRcTokenFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('rc') || '').trim();
    if (!raw || raw.length > 40 || !RC_PATTERN.test(raw)) return;
    window.sessionStorage.setItem('rendplex_rc_token', raw);
  } catch (_e) {
    // sessionStorage unavailable (private mode etc.) — silently ignore.
  }
}
import { Button } from '../components/ui/button';
import { TrendingUp, Share2, Zap, BarChart3, ArrowRight, Shield, Sparkles, X, DollarSign, Home, Smartphone, Check, Stethoscope } from 'lucide-react';
import { Footer } from '../components/LegalDisclaimers';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { RendPlexLogo, RendPlexLogoCompact } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import TutorialLauncher from '../components/tutorial/TutorialLauncher';

// Sample preview components for each feature
function PreviewInstantAnalysis() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
          <span className="text-xs text-slate-400 block mb-1">Purchase Price</span>
          <span className="font-primary font-bold text-[#12355B] dark:text-white">$425,000</span>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
          <span className="text-xs text-slate-400 block mb-1">Down Payment</span>
          <span className="font-primary font-bold text-[#12355B] dark:text-white">20%</span>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
          <span className="text-xs text-slate-400 block mb-1">Interest Rate</span>
          <span className="font-primary font-bold text-[#12355B] dark:text-white">5.25%</span>
        </div>
        <div className="bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
          <span className="text-xs text-slate-400 block mb-1">Monthly Rent</span>
          <span className="font-primary font-bold text-[#12355B] dark:text-white">$2,800</span>
        </div>
      </div>
      <div className="border-t border-slate-200 dark:border-slate-700 pt-4 space-y-2">
        <div className="flex justify-between"><span className="text-sm text-slate-500">Monthly Cash Flow</span><span className="font-bold text-[#45C04A]">+$847</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">Cap Rate</span><span className="font-bold text-[#12355B] dark:text-white">7.91%</span></div>
        <div className="flex justify-between"><span className="text-sm text-slate-500">ROI</span><span className="font-bold text-[#12355B] dark:text-white">11.96%</span></div>
      </div>
    </div>
  );
}

function PreviewDealScore() {
  return (
    <div className="text-center">
      <div className="relative inline-flex items-center justify-center w-32 h-32 mb-4">
        <svg className="w-32 h-32 -rotate-90" viewBox="0 0 128 128">
          <circle cx="64" cy="64" r="54" fill="none" stroke="currentColor" className="text-slate-100 dark:text-slate-800" strokeWidth="7" />
          <circle cx="64" cy="64" r="54" fill="none" stroke="#45C04A" strokeWidth="7" strokeLinecap="round"
            strokeDasharray={`${(87 / 100) * 339} 339`} style={{ filter: 'drop-shadow(0 0 8px rgba(0,200,83,0.4))' }} />
        </svg>
        <span className="absolute text-4xl font-primary font-black text-[#45C04A]">87</span>
      </div>
      <span className="inline-block bg-[#45C04A]/10 text-[#45C04A] text-sm font-bold px-4 py-1 rounded-full mb-3">Excellent Deal</span>
      <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Strong cash flow property with above-average ROI. Location score boosts overall rating. Favorable market conditions for long-term hold.</p>
    </div>
  );
}

function PreviewShareableCard() {
  return (
    <div className="bg-gradient-to-br from-[#12355B] to-[#1565C0] rounded-2xl p-5 text-white">
      <div className="flex items-center gap-2 mb-4">
        <RendPlexLogoCompact className="text-sm" />
        <span className="text-xs text-white/50 ml-auto">Deal Card</span>
      </div>
      <p className="font-primary font-bold text-sm mb-3 text-white/90">1234 Rue Saint-Denis, Montreal</p>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div className="bg-white/10 rounded-lg p-2 text-center">
          <span className="text-xs text-white/50 block">Score</span>
          <span className="font-bold text-[#45C04A]">87</span>
        </div>
        <div className="bg-white/10 rounded-lg p-2 text-center">
          <span className="text-xs text-white/50 block">Cash Flow</span>
          <span className="font-bold text-[#45C04A]">+$847</span>
        </div>
        <div className="bg-white/10 rounded-lg p-2 text-center">
          <span className="text-xs text-white/50 block">ROI</span>
          <span className="font-bold">11.9%</span>
        </div>
      </div>
      <div className="flex justify-between text-xs text-white/40">
        <span>$425,000</span>
        <span>Cap Rate: 7.91%</span>
      </div>
    </div>
  );
}

function PreviewDealLibrary() {
  const deals = [
    { addr: '1234 Rue Saint-Denis', score: 87, cf: '+$847', color: '#45C04A' },
    { addr: '5678 Ave du Parc', score: 72, cf: '+$423', color: '#45C04A' },
    { addr: '910 Boul. Pie-IX', score: 45, cf: '-$112', color: '#F59E0B' },
  ];
  return (
    <div className="space-y-3">
      {deals.map((d, i) => (
        <div key={i} className="flex items-center gap-3 bg-slate-50 dark:bg-slate-800 rounded-xl p-3">
          <div className="relative w-10 h-10 flex-shrink-0">
            <svg className="w-10 h-10 -rotate-90" viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="16" fill="none" stroke="currentColor" className="text-slate-200 dark:text-slate-700" strokeWidth="3" />
              <circle cx="20" cy="20" r="16" fill="none" stroke={d.color} strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(d.score / 100) * 100} 100`} />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-xs font-bold" style={{ color: d.color }}>{d.score}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#12355B] dark:text-white truncate">{d.addr}</p>
            <span className={`text-xs font-bold ${d.cf.startsWith('+') ? 'text-[#45C04A]' : 'text-red-500'}`}>{d.cf}/mo</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PreviewSuggestedOffer() {
  return (
    <div className="text-center">
      <div className="bg-[#45C04A]/5 border-2 border-[#45C04A]/20 rounded-2xl p-5 mb-4">
        <span className="text-sm text-slate-500 dark:text-slate-400 block mb-1">Suggested Offer Price</span>
        <span className="text-4xl font-primary font-black text-[#45C04A]">$398,500</span>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">6.2% below asking price of $425,000</p>
      </div>
      <div className="space-y-2 text-left">
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Check className="w-4 h-4 text-[#45C04A] flex-shrink-0" /> Based on comparable sales data
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Check className="w-4 h-4 text-[#45C04A] flex-shrink-0" /> Accounts for property condition
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <Check className="w-4 h-4 text-[#45C04A] flex-shrink-0" /> Optimized for target ROI
        </div>
      </div>
    </div>
  );
}

function PreviewMobile() {
  return (
    <div className="flex justify-center">
      <div className="w-48 bg-slate-50 dark:bg-slate-800 rounded-3xl border-4 border-slate-200 dark:border-slate-700 p-3 shadow-lg">
        <div className="bg-white dark:bg-[#131824] rounded-2xl p-3 space-y-3">
          <div className="flex items-center gap-1.5 mb-2">
            <div className="w-4 h-4 bg-[#12355B] rounded-md flex items-center justify-center">
              <TrendingUp className="w-2.5 h-2.5 text-[#45C04A]" />
            </div>
            <span className="text-[8px] font-bold"><span style={{ color: '#1565C0' }}>RendPle</span><span style={{ color: '#45C04A' }}>X</span></span>
          </div>
          <div className="text-center">
            <div className="relative inline-flex items-center justify-center w-14 h-14">
              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                <circle cx="28" cy="28" r="22" fill="none" stroke="currentColor" className="text-slate-100 dark:text-slate-700" strokeWidth="3" />
                <circle cx="28" cy="28" r="22" fill="none" stroke="#45C04A" strokeWidth="3" strokeLinecap="round" strokeDasharray={`${(87 / 100) * 138} 138`} />
              </svg>
              <span className="absolute text-sm font-black text-[#45C04A]">87</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex justify-between"><span className="text-[8px] text-slate-400">Cash Flow</span><span className="text-[8px] font-bold text-[#45C04A]">+$847</span></div>
            <div className="flex justify-between"><span className="text-[8px] text-slate-400">ROI</span><span className="text-[8px] font-bold text-[#12355B] dark:text-white">11.9%</span></div>
            <div className="flex justify-between"><span className="text-[8px] text-slate-400">Cap Rate</span><span className="text-[8px] font-bold text-[#12355B] dark:text-white">7.91%</span></div>
          </div>
          <div className="bg-[#12355B] rounded-lg py-1.5 text-center">
            <span className="text-[8px] font-bold text-white">Analyze Deal</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const PREVIEW_COMPONENTS = [
  PreviewInstantAnalysis,
  PreviewDealScore,
  PreviewShareableCard,
  PreviewDealLibrary,
  PreviewSuggestedOffer,
  PreviewMobile,
];

export default function Landing() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [previewIdx, setPreviewIdx] = useState(null);

  // Wave 1B.2A — capture `?rc=` attribution into sessionStorage on landing.
  // Attribution-only; validated server-side at signup. See §22.7.
  useEffect(() => {
    captureRcTokenFromUrl();
  }, []);

  const handleLogin = () => {
    navigate('/login');
  };

  const features = [
    { icon: BarChart3, titleKey: 'landing.featureInstantTitle', descKey: 'landing.featureInstantDesc' },
    { icon: Zap, titleKey: 'landing.featureScoreTitle', descKey: 'landing.featureScoreDesc' },
    { icon: Share2, titleKey: 'landing.featureShareTitle', descKey: 'landing.featureShareDesc' },
    { icon: TrendingUp, titleKey: 'landing.featureLibraryTitle', descKey: 'landing.featureLibraryDesc' },
    { icon: Shield, titleKey: 'landing.featureOfferTitle', descKey: 'landing.featureOfferDesc' },
    { icon: Stethoscope, titleKey: 'landing.featurePortfolioDoctorTitle', descKey: 'landing.featurePortfolioDoctorDesc', navigateTo: '/portfolio-doctor', testidSuffix: 'portfolio-doctor-preview' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      {/* Glassmorphism Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 backdrop-saturate-150 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <RendPlexLogo size="md" />
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher />
              <Button
                data-testid="nav-login-button"
                onClick={handleLogin}
                className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl px-6 py-2 font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button"
              >
                {t('nav.signIn')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-24 lg:py-36 px-4 sm:px-6 lg:px-12 overflow-hidden">
        {/* Background Property Image */}
        <div className="absolute inset-0">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/35251cdc-fe6f-4305-b40a-115bc9637197/images/5cac38517c03f4a4d3f18f4002c26542ed8d9f2720a2e70f5ab08875097ca05e.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F8FAFC]/95 via-[#F8FAFC]/85 to-[#F8FAFC]/60 dark:from-[#0B0E14]/95 dark:via-[#0B0E14]/90 dark:to-[#0B0E14]/70" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#45C04A]/10 dark:bg-[#45C04A]/20 rounded-full mb-6">
                <Sparkles className="w-4 h-4 text-[#45C04A]" />
                <span className="text-sm font-semibold text-[#45C04A]">AI-Powered Analysis</span>
              </div>
              <h1 className="font-primary font-extrabold text-5xl lg:text-7xl text-[#12355B] dark:text-white tracking-tighter leading-[1.05] mb-6">
                {t('landing.headline')}
              </h1>
              <p className="text-lg text-slate-500 dark:text-slate-400 mb-10 max-w-xl leading-relaxed">
                {t('landing.subheadline')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  data-testid="hero-cta-button"
                  onClick={handleLogin}
                  className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl px-8 py-6 text-lg font-semibold transition-all duration-300 hover:-translate-y-[1px] shadow-button group"
                >
                  {t('landing.cta')}
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
                <TutorialLauncher
                  variant="hero"
                  label={t('landing.seeHowItWorks')}
                  onCtaAnalyze={handleLogin}
                  className="rounded-xl px-8 py-6 text-lg"
                />
              </div>
              <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-400">
                <span>{t('landing.freePlanNote')} &middot; {t('landing.noCreditCard')}</span>
                <span className="hidden sm:inline">&middot;</span>
                <button
                  data-testid="hero-pricing-link"
                  onClick={() => navigate('/pricing')}
                  className="font-semibold text-[#12355B] dark:text-[#45C04A] hover:underline"
                >
                  {t('landing.viewPricing')}
                </button>
              </div>
            </div>

            {/* Hero Deal Card */}
            <div className="lg:col-span-5 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl shadow-card hover:shadow-card-hover transition-shadow duration-500 p-8">
                <div className="text-center mb-6">
                  <div className="relative inline-flex items-center justify-center w-28 h-28 mb-4">
                    <svg className="w-28 h-28 -rotate-90" viewBox="0 0 120 120">
                      <circle cx="60" cy="60" r="52" fill="none" stroke="currentColor" className="text-slate-100 dark:text-slate-800" strokeWidth="8" />
                      <circle cx="60" cy="60" r="52" fill="none" stroke="#45C04A" strokeWidth="8" strokeLinecap="round"
                        strokeDasharray={`${(92 / 100) * 327} 327`} />
                    </svg>
                    <span className="absolute text-4xl font-primary font-extrabold text-[#12355B] dark:text-white">92</span>
                  </div>
                  <p className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{t('landing.dealScore')}</p>
                </div>
                <div className="space-y-0">
                  <div className="flex justify-between items-center py-4 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-slate-500 dark:text-slate-400">{t('landing.monthlyCashFlow')}</span>
                    <span className="font-semibold text-[#45C04A] text-lg">+$1,247</span>
                  </div>
                  <div className="flex justify-between items-center py-4 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-slate-500 dark:text-slate-400">{t('landing.roi')}</span>
                    <span className="font-semibold text-[#12355B] dark:text-white text-lg">15.8%</span>
                  </div>
                  <div className="flex justify-between items-center py-4">
                    <span className="text-slate-500 dark:text-slate-400">{t('landing.suggestedOffer')}</span>
                    <span className="font-semibold text-[#12355B] dark:text-white text-lg">$285K</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 lg:py-32 px-4 sm:px-6 lg:px-12 bg-white dark:bg-[#0B0E14]/50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-16">
            <h2 className="font-primary font-bold text-3xl lg:text-4xl text-[#12355B] dark:text-white tracking-tight mb-4">
              {t('landing.featuresTitle')}
            </h2>
            <p className="text-lg text-slate-500 dark:text-slate-400 leading-relaxed">
              {t('landing.featuresSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                data-testid={feature.testidSuffix ? `feature-card-${feature.testidSuffix}` : `feature-card-${idx}`}
                onClick={() => {
                  if (feature.navigateTo) { navigate(feature.navigateTo); return; }
                  setPreviewIdx(idx);
                }}
                className="bg-[#F8FAFC] dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8 hover:shadow-card-hover transition-all duration-300 hover:-translate-y-[2px] group cursor-pointer select-none"
                style={{ animationDelay: `${idx * 0.1}s` }}
              >
                <div className="w-12 h-12 bg-[#12355B] dark:bg-[#45C04A]/10 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-105">
                  <feature.icon className="w-6 h-6 text-white dark:text-[#45C04A]" />
                </div>
                <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white mb-3">
                  {t(feature.titleKey)}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 leading-relaxed">
                  {t(feature.descKey)}
                </p>
                <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#45C04A] opacity-0 group-hover:opacity-100 transition-opacity">
                  {t('landing.featureCardSeePreview')} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About + Support (public-facing business description + contact) */}
      <section
        id="about"
        data-testid="landing-about-support"
        className="py-20 px-4 sm:px-6 lg:px-12 bg-[#F8FAFC] dark:bg-[#0B0E14]"
      >
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
          <div
            data-testid="landing-about-block"
            className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8"
          >
            <h2 className="font-primary font-bold text-2xl text-[#12355B] dark:text-white mb-3">
              {t('landing.aboutTitle')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              {t('landing.aboutBody')}
            </p>
          </div>
          <div
            id="support"
            data-testid="landing-support-block"
            className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8"
          >
            <h2 className="font-primary font-bold text-2xl text-[#12355B] dark:text-white mb-3">
              {t('landing.supportTitle')}
            </h2>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
              {t('landing.supportBody')}
            </p>
            <p className="text-slate-700 dark:text-slate-300">
              {t('landing.supportEmailLabel')}{' '}
              <a
                href="mailto:support@rendplex.com"
                data-testid="landing-support-email"
                className="text-[#45C04A] font-semibold underline"
              >
                support@rendplex.com
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-12 bg-[#12355B] dark:bg-[#131824] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#45C04A]/5 via-transparent to-[#45C04A]/10" />
        <div className="max-w-3xl mx-auto text-center relative">
          <h2 className="font-primary font-bold text-3xl lg:text-4xl text-white tracking-tight mb-6">
            {t('landing.ctaTitle')}
          </h2>
          <p className="text-lg text-slate-300 mb-10">
            {t('landing.ctaSubtitle')}
          </p>
          <Button
            data-testid="cta-get-started-button"
            onClick={handleLogin}
            className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-10 py-6 text-lg font-bold transition-all duration-300 hover:-translate-y-[1px] shadow-button"
          >
            {t('landing.ctaButton')}
          </Button>
        </div>
      </section>

      <Footer />

      {/* Feature Preview Modal */}
      {previewIdx !== null && (() => {
        const PreviewComponent = PREVIEW_COMPONENTS[previewIdx];
        const feature = features[previewIdx];
        return (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={() => setPreviewIdx(null)}
          >
            <div
              className="bg-white dark:bg-[#131824] rounded-2xl shadow-2xl max-w-md w-full relative animate-fade-in overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#12355B] dark:bg-[#45C04A]/10 rounded-xl flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-white dark:text-[#45C04A]" />
                  </div>
                  <h3 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">{t(feature.titleKey)}</h3>
                </div>
                <button
                  data-testid="close-preview-modal"
                  onClick={() => setPreviewIdx(null)}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-white transition-colors p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Preview Content */}
              <div className="px-6 py-6">
                <PreviewComponent />
              </div>

              {/* Footer CTA */}
              <div className="px-6 pb-6 pt-2">
                <Button
                  data-testid="preview-cta-button"
                  onClick={handleLogin}
                  className="w-full bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-xl py-3 font-semibold transition-all duration-300"
                >
                  {t('landing.cta')} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
